<script lang="ts">
  import * as Dialog from "$lib/components/ui/dialog";
  import * as Form from "$lib/components/ui/form";
  import { Input } from "$lib/components/ui/input";
  import { Checkbox } from "$lib/components/ui/checkbox";
  import { zodClient } from "sveltekit-superforms/adapters";
  import { bootcampDelivery } from "$lib/client/stores/nucamp-bootcampdelivery";
  import { user } from "$lib/client/stores/nucamp-user";
  import { cancelPopUp } from "$lib/client/stores/nucamp-popup";
  import dayjs from "dayjs";
  import advancedFormat from 'dayjs/plugin/advancedFormat';
  import { toast } from "svelte-sonner";
  import { type SuperValidated, type Infer, superForm } from "sveltekit-superforms";
  import { contactCaptureSchema, type ContactCaptureSchema } from "../forms/CaptureContact.svelte";
  import { type Unsubscriber } from "svelte/store";
  import { onDestroy } from "svelte";
  import { followBootcamp } from "$lib/client/services/user";

  dayjs.extend(advancedFormat)

  export let open = false;
  export let onOpenChange: any;
  export let supForm: SuperValidated<Infer<ContactCaptureSchema>>;

  const subscriptions: Unsubscriber[] = [];

  const form = superForm(supForm, {
    validators: zodClient(contactCaptureSchema),
    dataType: "json",
    warnings: {
      duplicateId: false
    },
    applyAction: false,
    invalidateAll: false,
    resetForm: false,
    async onResult({ result }) {
      if (result.type !== "success") {
        console.error("The form is invalid");
        toast.error("The form is invalid", {
          description: "Please fill in all required fields and try again.",
          action: {
            label: "Ok",
            onClick: () => {}
          }
        });
      } else {
        const loadingId = toast.loading("Please wait...", {duration: Number.POSITIVE_INFINITY});
        const validatedUser = result?.data?.extData.user;
        const bootcampDelivery = result?.data?.extData.selBootcampDelivery;
        followBootcamp(validatedUser, bootcampDelivery).then(() => {
          toast.dismiss(loadingId);
          toast.success("Thank you for your interest in Nucamp!", {
            description: "We will be in touch with you soon.",
            duration: 5000,
            action: {
              label: "Ok",
              onClick: () => {}
            }
          });
        })
        open = false;
      }
    },
  });
  
  const { form: formData, enhance } = form;

  subscriptions.push(bootcampDelivery.subscribe((bcDelivery) => {
    if (bcDelivery) {
      $formData.preferredBootcamp.id = bcDelivery.bootcamp.id;
      $formData.preferredBootcamp.title = `${bcDelivery.title} (${bcDelivery.length})`;
      $formData.preferredCommunity = bcDelivery.community.id;
    }
  }));

  $: if ($user?.email || $user?.displayName || $user?.phone || $user?.phoneId) {
    if ($user?.email && !$formData.email) {
      $formData.email = $user.email;
    }
    if ($user?.displayName && !$formData.displayName) {
      $formData.displayName = $user.displayName;
    }
    if ($user?.phoneId && !$formData.phone) {
      $formData.phone = $user.phoneId;
    } else if ($user?.phone && !$formData.phone) {
      $formData.phone = $user.phone;
    }
  }

  onDestroy(() => {
    subscriptions.forEach((s:any) => s());
  });
</script>


<Dialog.Root bind:open bind:onOpenChange closeOnOutsideClick={false} closeOnEscape={false}>
  {#if $bootcampDelivery}
    <Dialog.Content class="p-0 max-w-[90%] max-h-[90%] sm:max-w-[60%] md:max-w-[55%] lg:max-w-[40%] xl:max-w-[30%] flex flex-col bg-base-100 dark:bg-slate-800 dark:border-slate-700">
      <Dialog.Header class="p-4 pb-0">
          <Dialog.Title>
            <h3 class="text-2xl/6 pr-4 md:text-3xl text-nucamp-{$bootcampDelivery?.name} font-bold">{$bootcampDelivery?.title}</h3>
            <p class="text-lg/6 mt-2">{dayjs($bootcampDelivery?.startdate).format("MMMM Do YYYY")} to {dayjs($bootcampDelivery?.enddate).format("MMMM Do YYYY")}</p>
          </Dialog.Title>
      </Dialog.Header>
      <div class="px-4 flex-1 overflow-y-auto">
        <Dialog.Description>
          <div class="bg-slate-100 dark:bg-slate-600 rounded-sm text-center my-4 p-4">
            <p class="text-lg pb-4 font-bold dark:text-white">Interested?</p>
            <p class="text-md dark:text-white">Learn more and plan to start your journey on {dayjs($bootcampDelivery?.startdate).format("MMMM Do")}</p>
          </div>
        </Dialog.Description>
        <form method="POST" class="pt-4 mb-24 md:mb-4" action="/capturecontact/?/capturecontact" use:enhance>
          <Form.Field {form} name="displayName">
            <Form.Control let:attrs>
              <Form.Label class="text-nucamp-{$bootcampDelivery?.name} font-bold">Your First and Last Name*</Form.Label>
              <Input 
                {...attrs} 
                autocomplete="name" 
                id="displayName" 
                name="displayName"
                placeholder="First and Last Name"
                bind:value={$formData.displayName} 
                on:blur={() => cancelPopUp()} 
                on:focus={() => cancelPopUp()}
                class="dark:bg-slate-600"
              />
            </Form.Control>
            <Form.FieldErrors class="!mt-0"/>
          </Form.Field>
          <Form.Field {form} class="mt-2" name="email">
            <Form.Control let:attrs>
              <Form.Label class="text-nucamp-{$bootcampDelivery?.name} font-bold">Your Email*</Form.Label>
              <Input 
                {...attrs}
                autocomplete="email" 
                id="email" 
                name="email"
                placeholder="email@domain.com"
                bind:value={$formData.email} 
                on:blur={() => cancelPopUp()} 
                on:focus={() => cancelPopUp()}
                class="dark:bg-slate-600"
              />
            </Form.Control>
            <Form.FieldErrors class="!mt-0"/>
          </Form.Field>
          <Form.Field {form} class="mt-2" name="phone">
            <Form.Control let:attrs>
              <Form.Label class="text-nucamp-{$bootcampDelivery?.name} font-bold">Your Phone Number*</Form.Label>
              <Input {...attrs} bind:value={$formData.phone} on:blur={() => cancelPopUp()} class="dark:bg-slate-600"/>
            </Form.Control>
            <Form.FieldErrors class="!mt-0"/>
          </Form.Field>
          <!-- <Form.Field {form} class="mt-2" name="acceptTerms">
              <Form.Control let:attrs>
                <Form.Label class="text-nucamp-{$bootcampDelivery?.name} font-bold">I Agree*</Form.Label>
                <Checkbox {...attrs} bind:checked={$formData.acceptTerms}  class="dark:bg-slate-600"/>
              </Form.Control>
              <Form.FieldErrors class="!mt-0"/>
          </Form.Field> 
          <p class="text-nucamp-{$bootcampDelivery?.name} mt-2  text-[8px]">*By checking "I Agree", you are opting-in to receive information, including text 
            messages from Nucamp. You also agree to the following <a href="/termsofuse" class="underline underline-offset-2" target="_blank">Terms of use</a>, <a href="/smstermsofuse" class="underline underline-offset-2" target="_blank">SMS Terms of use</a> & <a href="/privacy" class="underline underline-offset-2" target="_blank">Privacy Policy</a>. 
            Reply STOP to stop receiving text messages.</p>  -->
          <Form.Button class="mt-4 text-slate-100 bg-nucamp-{$bootcampDelivery?.name} hover:bg-nucamp-{$bootcampDelivery?.name}-hover">Submit</Form.Button>
        </form>
      </div>
      <Dialog.Close class="text-nucamp-{$bootcampDelivery?.name} hover:text-nucamp-{$bootcampDelivery?.name}-hover"></Dialog.Close>
    </Dialog.Content>
  {/if}
</Dialog.Root>